// @ts-nocheck
import { createContext, useContext, useEffect, useState, ReactNode, useMemo, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { getUserCompleteProfile } from '../utils/userProfileCoordinator';
import { toast } from 'sonner';
import { ChatMessageCache } from '@/services/ChatMessageCache';
import { clearProfileCaches } from '@/pages/Profile';
import { logger } from '../utils/productionLogger';
import { updateUserLastActive } from '../utils/lastActiveUpdater';
import { IntegrityService } from '@/services/security/IntegrityService';
import { PlayIntegrityService } from '@/services/PlayIntegrityService';
import { Capacitor } from '@capacitor/core';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import type { IntegrityStatus } from '@/types/integrity';
import { trackEvent, identifyUser, resetUser, AnalyticsEvents } from '@/analytics';
import { badgeService } from '@/services/push/badgeService';
import { pushService } from '@/services/push/pushService';
import { blockPrematureIOSOAuthClose, setIOSOAuthLock } from '@/lib/iosOAuthLock';
import {
  isAppleReviewRoute,
  shouldUseReviewerAuth,
} from '@/lib/reviewer-auth';
import {
  AUTH_REMOTE_SIGNOUT_TIMEOUT_MS,
  createAuthSessionGuard,
  logAuthEvent,
  raceWithTimeout,
  type AuthResolution,
} from '@/utils/authSessionGuard';

// V315.4: Debounce Play Integrity on SIGNED_IN (avoid double runs / TOKEN_REFRESHED noise)
const integrityLoginDebounce = new Map<string, number>();
const INTEGRITY_LOGIN_TTL_MS = 30_000;

// V315.8: Idempotent guard to prevent double bootstrap (getSession + INITIAL_SESSION)
let didAttemptBootstrapThisLaunch = false;

// P0: Single-flight ensure-user-row per user per launch (dedupe INITIAL_SESSION + SIGNED_IN races)
const ensureUserRowPromises = new Map<string, Promise<void>>();

/**
 * Invokes ensure-user-row at most once per user id per app launch (concurrent callers share one promise).
 * On failure, clears the map entry so a later SIGNED_IN can retry.
 */
function ensureUserRowOncePerLaunch(session: Session, source: string): Promise<void> {
  const uid = session.user.id;
  const existing = ensureUserRowPromises.get(uid);
  if (existing) {
    if (import.meta.env.DEV) {
      console.log(`[Auth] ensure-user-row single-flight: awaiting in-flight promise uid=${uid.slice(0, 8)}… source=${source}`);
    }
    return existing;
  }

  const p = (async () => {
    try {
      const { data, error } = await supabase.functions.invoke('ensure-user-row', {
        body: {
          userId: session.user.id,
          userEmail: session.user.email || '',
          userData: {
            name: session.user.user_metadata?.name || session.user.user_metadata?.full_name || session.user.email?.split('@')[0],
            onboarding_complete: false,
            avatar_url: session.user.user_metadata?.avatar_url || session.user.user_metadata?.picture
          }
        }
      });
      if (error) {
        console.warn(`⚠️ P0 ensure-user-row failed (non-blocking) source=${source}:`, error);
        ensureUserRowPromises.delete(uid);
      } else {
        console.log(`✅ P0 ensure-user-row success source=${source}:`, data);
      }
    } catch (err) {
      console.warn(`⚠️ P0 ensure-user-row exception (non-blocking) source=${source}:`, err);
      ensureUserRowPromises.delete(uid);
    }
  })();

  ensureUserRowPromises.set(uid, p);
  return p;
}

// P0: Dedupe profile coordinator + default-photo bootstrap when INITIAL_SESSION and SIGNED_IN fire close together
let lastProfileBootstrapUid: string | null = null;
let lastProfileBootstrapAt = 0;
const PROFILE_BOOTSTRAP_DEBOUNCE_MS = 800;

function shouldSkipProfileBootstrap(uid: string): boolean {
  const now = Date.now();
  if (lastProfileBootstrapUid === uid && now - lastProfileBootstrapAt < PROFILE_BOOTSTRAP_DEBOUNCE_MS) {
    if (import.meta.env.DEV) {
      console.log(`[Auth] profile bootstrap skipped (debounce ${PROFILE_BOOTSTRAP_DEBOUNCE_MS}ms) uid=${uid.slice(0, 8)}…`);
    }
    return true;
  }
  lastProfileBootstrapUid = uid;
  lastProfileBootstrapAt = now;
  return false;
}

function resetProfileBootstrapDedupe(): void {
  lastProfileBootstrapUid = null;
  lastProfileBootstrapAt = 0;
}

// V316.5: In-memory guard to prevent login-loop spam (only 1 login check per user per app runtime)
let lastLoginIntegrityUserId: string | null = null;

// V315.5: Deep debug for auth-state integrity (DEV or pi_debug=1)
const AUTH_PI_DEBUG = typeof import.meta !== 'undefined' && (import.meta.env?.DEV === true || (typeof window !== 'undefined' && new URLSearchParams(window.location?.search || '').get('pi_debug') === '1'));
function shouldRunLoginIntegrity(session: Session): boolean {
  const userId = session?.user?.id;
  if (!userId) return false;

  // V316.5: Only run once per user per app runtime
  if (lastLoginIntegrityUserId === userId) {
    if (AUTH_PI_DEBUG) {
      console.log('[Auth] V316.5: Login integrity skipped (already ran for this user this runtime)');
    }
    return false;
  }

  // V315.4: Legacy debounce (keep for safety, but runtime guard is primary)
  const key = session?.access_token || userId;
  const last = integrityLoginDebounce.get(key);
  const now = Date.now();
  if (last != null && now - last < INTEGRITY_LOGIN_TTL_MS) return false;
  integrityLoginDebounce.set(key, now);

  // Mark this user as having run login integrity
  lastLoginIntegrityUserId = userId;
  return true;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isReady: boolean; // 🚀 V97.0 SIMPLIFIED: Single ready state
  isLoading: boolean; // Add missing property
  /** Explicit: pending (bootstrap) | authenticated | logged_out (local logout terminal). */
  authResolution: AuthResolution;
  error: string | null;
  userProfile: any | null;
  refreshProfile: () => Promise<void>; // V189.1: Refresh profile without full reload
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  googleSignIn: () => Promise<{ error: any }>;
  appleSignIn: () => Promise<{ error: any }>; // V335: Sign in with Apple (App Store compliance)
  resetAuthInProgress: (reason?: string) => void; // V404: Clear internal isAuthenticating lock from OAuth UI reset paths
  /** Clear post-logout session suppress — call before any intentional auth (incl. direct OAuth). */
  beginIntentionalAuth: () => void;
  /** Active auth generation — bump on logout; authenticated async must check before mutating. */
  getAuthGeneration: () => number;
  isAuthGenerationActive: (generation: number) => boolean;
}

// Create context with proper default values to prevent build errors
const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  isReady: false, // 🚀 V97.0 SIMPLIFIED: Single ready state
  isLoading: false, // Add missing property
  authResolution: 'pending',
  error: null,
  userProfile: null,
  refreshProfile: async () => { }, // V189.1: Refresh profile
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => { },
  googleSignIn: async () => ({ error: null }),
  appleSignIn: async () => ({ error: null }), // V335: Sign in with Apple default
  resetAuthInProgress: () => { }, // V404: no-op default
  beginIntentionalAuth: () => { },
  getAuthGeneration: () => 0,
  isAuthGenerationActive: () => true,
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isReady, setIsReady] = useState(false); // 🚀 V97.0 SIMPLIFIED: Single ready state
  const [isLoading, setIsLoading] = useState(true); // Add missing state
  const [authResolution, setAuthResolution] = useState<AuthResolution>('pending');
  const [error, setError] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<any | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false); // 🔐 V330: Prevent overlapping auth attempts

  // V333.5: Fix stale closure inside onAuthStateChange (effect has [] deps)
  const currentUserIdRef = useRef<string | null>(null);
  const authGuardRef = useRef(createAuthSessionGuard());
  /** After local logout, ignore session restores until intentional sign-in begins. */
  const suppressSessionRestoreRef = useRef(false);

  useEffect(() => {
    currentUserIdRef.current = user?.id ?? null;
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || !userProfile?.id) return;

    void pushService.initializeForUser(user.id).catch((err) => {
      console.warn('⚠️ Push listener init failed:', err);
    });
  }, [user?.id, userProfile?.id]);

  useEffect(() => {
    // 🔧 V102.9 FIX: Initialize authentication state check
    logger.debug('🔧 V102.9: Initializing authentication system');

    // Bootstrap from first real session (getSession or INITIAL_SESSION) — exactly once per launch
    const maybeQueueBootstrap = (session: Session | null, source: string) => {
      if (!session) return;
      if (didAttemptBootstrapThisLaunch) return;
      didAttemptBootstrapThisLaunch = true;
      console.log(`[Auth] bootstrap -> integrity bootstrap queued source=${source}`);
      // V317.0: Bootstrap with 'auth_init' source for Truth Serum tracking
      PlayIntegrityService.run('bootstrap', 'auth_init')
        .then((r) => {
          if (r != null) console.log('[Integrity] bootstrap result ok=', r.ok, 'reason=', r.reason, 'check_id=', r.check_id ?? '');
        })
        .catch((e) => console.error('[Integrity] bootstrap failed', e));
    };

    /** P0: Single path for coordinator + default-photo bootstrap after ensure-user-row (deduped + chained from ensure). */
    const loadProfileCoordinatorBootstrap = (session: Session) => {
      if (shouldSkipProfileBootstrap(session.user.id)) return;
      setTimeout(async () => {
        try {
          console.log('🚀 V161.11: Loading user profile via coordinator');
          const profile = await getUserCompleteProfile(session.user.id);

          const { hasRealCloudinaryPhoto } = await import('../utils/sanitizeUserUpdate');
          if (profile && (!profile.photo_urls || profile.photo_urls.length === 0) && !hasRealCloudinaryPhoto(profile)) {
            const { ensureDefaultProfilePhoto } = await import('../lib/defaultProfilePhoto');
            ensureDefaultProfilePhoto(
              session.user.id,
              session.user,
              (previewUrl) => {
                if (previewUrl && !previewUrl.startsWith('blob:') && !previewUrl.startsWith('data:')) {
                  console.log('✅ V244: Default photo cached in PhotoStore, updating profile state with non-blob URL');
                  setUserProfile({ ...profile, photo_urls: [previewUrl] });
                } else {
                  console.log('✅ V244: Default photo cached in PhotoStore (blob URL - not updating profile state to prevent DB write)');
                }
              }
            ).catch((err) => {
              console.warn('⚠️ V244: Default photo initialization failed (non-critical):', err);
            });
          } else if (profile && hasRealCloudinaryPhoto(profile)) {
            console.log('✅ V213.5: User already has Cloudinary photo - skipping default bootstrap');
          }

          if (!profile) {
            logger.debug('🚨 V161.11: No profile found - creating minimal profile for onboarding');

            const { getDefaultGoogleImageUrl } = await import('../lib/defaultProfilePhoto');
            const defaultUrl = getDefaultGoogleImageUrl(session.user);

            const minimalProfile = {
              id: session.user.id,
              email: session.user.email,
              name: null,
              dob: null,
              gender: 'other',
              bio: '',
              photo_urls: defaultUrl ? [defaultUrl] : [],
              account_status: 'ACTIVE',
              trust_score: 0,
              is_verified: false,
              is_emergency_bypass: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };

            if (defaultUrl) {
              const { ensureDefaultProfilePhoto } = await import('../lib/defaultProfilePhoto');
              ensureDefaultProfilePhoto(session.user.id, session.user).catch((err) => {
                console.warn('⚠️ V242.3: Default photo upload failed (non-critical):', err);
              });
            }

            setUserProfile(minimalProfile);
            setIsReady(true);
            logger.debug('🚨 V161.11: Empty profile created to force onboarding completion');
            return;
          }

          if (profile) {
            setUserProfile(profile);
            setIsReady(true);
            logger.debug('✅ V161.11: User profile loaded via coordinator');
          } else {
            logger.debug('⚠️ User not in users table - creating minimal profile for onboarding');

            const minimalProfile = {
              id: session.user.id,
              email: session.user.email,
              name: null,
              dob: null,
              gender: 'other',
              bio: '',
              location: '',
              photo_urls: session.user.user_metadata?.avatar_url ? [session.user.user_metadata.avatar_url] : [],
              account_status: 'ACTIVE',
              trust_score: 0,
              is_verified: false,
              is_emergency_bypass: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };

            setUserProfile(minimalProfile);
            setIsReady(true);
            logger.debug('📋 Empty profile created to force onboarding completion');
          }
        } catch (err) {
          logger.error('Error fetching user profile:', err);

          try {
            const emergencyProfile = {
              id: session.user.id,
              email: session.user.email,
              name: null,
              dob: null,
              gender: 'other',
              bio: '',
              location: '',
              photo_urls: session.user.user_metadata?.avatar_url ? [session.user.user_metadata.avatar_url] : [],
              account_status: 'ACTIVE',
              trust_score: 0,
              is_verified: false,
              is_emergency_bypass: true,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };

            setUserProfile(emergencyProfile);
            setIsReady(true);
            logger.debug('🚨 FINAL EMERGENCY BYPASS: Empty profile created to force onboarding completion');
          } catch (emergencyErr) {
            logger.error('❌ FINAL EMERGENCY BYPASS failed:', emergencyErr);
            setIsReady(true);
          }
        }
      }, 0);
    };

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        const hasSession = !!session;

        // V330: Enhanced auth event logging with user ID tracking (V333.5: use ref to avoid stale closure)
        const oldUserId = currentUserIdRef.current;
        const newUserId = session?.user?.id;
        const userChanged = oldUserId && newUserId && oldUserId !== newUserId;

        console.log(`🔐 V330: [Auth Event] event=${event} hasSession=${hasSession}`);
        if (userChanged) {
          console.log(`🔐 V330: ⚠️ USER SWITCH DETECTED!`);
          console.log(`🔐 V330: Old user: ${oldUserId}`);
          console.log(`🔐 V330: New user: ${newUserId}`);
        }
        logger.debug(`🔧 V102.9: Auth state change - Event: ${event}, Session: ${hasSession}`);

        // V315.5: Observable integrity – log why checks do/don't run (DEV or pi_debug=1)
        if (AUTH_PI_DEBUG) {
          const platform = Capacitor.getPlatform();
          const isNative = Capacitor.isNativePlatform();
          const piShouldRun = PlayIntegrityService.shouldRun();
          console.log(`[Auth] onAuthStateChange event=${event} hasSession=${hasSession} platform=${platform} isNative=${isNative} shouldRun=${piShouldRun}`);
        }

        // After local-first logout, ignore session restores until intentional sign-in.
        if (
          suppressSessionRestoreRef.current &&
          session?.user &&
          event !== 'SIGNED_OUT'
        ) {
          logAuthEvent('STALE_AUTH_RESULT_IGNORED', { event, reason: 'post-logout-suppress' });
          if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
            setIsLoading(false);
            setIsReady(true);
            setAuthResolution('logged_out');
          }
          return;
        }

        // Clear caches when user changes or signs out
        if (event === 'SIGNED_OUT') {
          resetProfileBootstrapDedupe();
          // Clear cache for the user that's signing out (V333.5: use ref to avoid stale closure)
          const userId = session?.user?.id || currentUserIdRef.current;
          if (userId) {
            ChatMessageCache.clearAllForUser(userId);
          }
          setAuthResolution('logged_out');
        } else if (event === 'SIGNED_IN') {
          // Clear cache if switching users (V333.5: use ref to avoid stale closure)
          const currentUserId = currentUserIdRef.current;
          const newUserId = session?.user?.id;
          if (currentUserId && newUserId && currentUserId !== newUserId) {
            ChatMessageCache.clearAllForUser(currentUserId);
          }
          // V315.4: Trigger Play Integrity on fresh login (debounced; not on TOKEN_REFRESHED)
          if (session && PlayIntegrityService.shouldRun() && shouldRunLoginIntegrity(session)) {
            console.log('[Auth] SIGNED_IN -> integrity login check queued');
            // V317.0: Login with 'auth_event' source for Truth Serum tracking
            PlayIntegrityService.run('login', 'auth_event')
              .then((r) => {
                if (r != null) console.log('[Integrity] login result ok=', r.ok, 'reason=', r.reason, 'check_id=', r.check_id ?? '');
              })
              .catch((e) => console.error('[Integrity] login failed', e));
          } else if (session) {
            if (!PlayIntegrityService.shouldRun()) {
              if (import.meta.env.DEV) console.log('[Auth] SIGNED_IN -> PI skipped (shouldRun=false) - no integrity row will be created');
            } else if (!shouldRunLoginIntegrity(session) && AUTH_PI_DEBUG) {
              console.log('[Auth] SIGNED_IN -> integrity skipped (debounce)');
            }
          }
        }

        // V208.0: Analytics - user_signed_in + identify
        if (event === 'SIGNED_IN' && session?.user) {
          trackEvent(AnalyticsEvents.USER_SIGNED_IN).catch(() => {});
          identifyUser(session.user.id).catch(() => {});
        }

        if (event === 'INITIAL_SESSION') {
          maybeQueueBootstrap(session, 'INITIAL_SESSION');
          // V333.4: Only update when session exists; when null, let getSession be authoritative
          // (prevents setting user=null before getSession completes → auth redirect loop)
          if (session) {
            setSession(session);
            setUser(session.user);
          }
        } else {
          setSession(session);
          setUser(session?.user ?? null);
        }

        // V330: Reset isAuthenticating when auth completes (success or failure)
        if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
          console.log(`🔐 V330: Resetting isAuthenticating (event: ${event})`);
          setIsAuthenticating(false);
        }

        if (session?.user) {
          // 🔧 V102.9 FIX: Set loading false immediately when we have a session
          setIsLoading(false);
          if (event !== 'INITIAL_SESSION') {
            // INITIAL_SESSION: keep authResolution pending until getSession is authoritative
            setAuthResolution('authenticated');
          }

          // P2: last_active only on session establishment — not TOKEN_REFRESHED / USER_UPDATED
          if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
            updateUserLastActive(session.user.id).catch(err => {
              console.warn('⚠️ Failed to update last_active on auth:', err);
            });
          }

          // P0: ensure-user-row + profile coordinator only on session establishment — not TOKEN_REFRESHED / USER_UPDATED / etc.
          const shouldBootstrapRowAndProfile = event === 'INITIAL_SESSION' || event === 'SIGNED_IN';

          if (shouldBootstrapRowAndProfile) {
            void ensureUserRowOncePerLaunch(session, event).then(() => {
              loadProfileCoordinatorBootstrap(session);
            });
          }
        } else {
          // 🔧 V102.9 FIX: Set loading false when no session
          setIsLoading(false);
          setUserProfile(null);
          // V333.4: Don't set isReady on INITIAL_SESSION - let getSession be authoritative
          if (event !== 'INITIAL_SESSION') {
            setIsReady(true);
            setAuthResolution('logged_out');
          }
          logger.debug('🔧 V102.9: No session - authentication ready');
        }
      }
    );

    // 🔧 V102.9 FIX: Check for existing session and handle loading state properly
    // V315.8: Pull-based bootstrap using getSession() on app start
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      const platform = Capacitor.getPlatform();
      const hasSession = !!session;

      // V315.8: Production-safe log (no tokens, no PII)
      console.log(`[Auth] startup getSession hasSession=${hasSession} platform=${platform}`);
      logger.debug('🔧 V102.9: Initial session check', { hasSession, error });

      if (error) {
        logger.error('🔧 V102.9: Session check error:', error);
        setIsLoading(false);
        setIsReady(true);
        setAuthResolution('logged_out');
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);

      // 🔧 V102.9 FIX: Always set loading to false after initial session check
      setIsLoading(false);
      setAuthResolution(session?.user ? 'authenticated' : 'logged_out');

      maybeQueueBootstrap(session, 'getSession');

      // P0: Same ensure → coordinator chain as INITIAL_SESSION/SIGNED_IN (deduped; covers edge cases where listener order skips bootstrap).
      if (session?.user) {
        void ensureUserRowOncePerLaunch(session, 'getSession').then(() => {
          loadProfileCoordinatorBootstrap(session);
        });
      }

      // V333.4: Set isReady after getSession resolves for BOTH cases (session or not)
      // App must not redirect to /auth while localStorage has session; profile loads async
      setIsReady(true);
      logger.debug('🔧 V102.9: getSession complete - ready', { hasSession: !!session });
    }).catch((err) => {
      logger.error('🔧 V102.9: Session check failed:', err);
      setIsLoading(false);
      setIsReady(true);
      setAuthResolution('logged_out');
    });

    return () => subscription.unsubscribe();
  }, []);

  // V157.3 TASK 4: Stabilize signIn function
  const signIn = useCallback(async (email: string, password: string) => {
    suppressSessionRestoreRef.current = false;
    // ─────────────────────────────────────────────────────────────────────────
    // ⚠️  APPLE_REVIEW_OTP_BYPASS — REVIEW-ONLY GUARD
    // App Store review path only. Credentials validated server-side via Edge Function.
    // ─────────────────────────────────────────────────────────────────────────
    if (shouldUseReviewerAuth()) {
      if (import.meta.env.DEV) {
        console.log('[AppleReview] reviewer auth path selected', {
          platform: Capacitor.getPlatform(),
          viaReviewRoute: isAppleReviewRoute(),
        });
      }
      console.log('🍎 [AppleReview] Edge session path (server-side credential validation)');
      try {
        setError(null);

        const { data: bypassData, error: bypassError } = await supabase.functions.invoke(
          'apple-review-auth',
          { body: { email, password } },
        );

        let edgeBodyErr = '';
        if (bypassData && typeof bypassData === 'object' && 'error' in bypassData) {
          edgeBodyErr = String((bypassData as { error?: string }).error ?? '');
        }
        const edgeMsg = bypassError?.message ?? edgeBodyErr;

        if (bypassError || !bypassData?.success || !bypassData?.session) {
          const msg =
            edgeMsg ||
            'Review sign-in failed. Deploy apple-review-auth Edge Function (Supabase Dashboard).';
          console.error('🍎 [AppleReview] Edge failed:', msg, bypassError ?? bypassData);
          setError(msg);
          return { error: new Error(msg) };
        }

        const { error: sessionError } = await supabase.auth.setSession({
          access_token:  bypassData.session.access_token,
          refresh_token: bypassData.session.refresh_token,
        });

        if (sessionError) {
          console.error('🍎 [AppleReview] setSession failed:', sessionError.message);
          setError(sessionError.message);
          return { error: sessionError };
        }

        console.log('🍎 [AppleReview] ✅ Session set — review account signed in');
        return { error: null };
      } catch (err: any) {
        const error = err.message || 'Review sign-in exception';
        setError(error);
        return { error };
      }
    }
    // ── END APPLE_REVIEW_OTP_BYPASS ──────────────────────────────────────────

    // Normal sign-in path — all non-review accounts go through here unchanged.
    try {
      setError(null);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error };
    } catch (err: any) {
      const error = err.message || 'Sign in failed';
      setError(error);
      return { error };
    }
  }, []);

  // V157.3 TASK 4: Stabilize signUp function
  // V314.2: Enhanced with Play Integrity verification
  const signUp = useCallback(async (email: string, password: string) => {
    try {
      suppressSessionRestoreRef.current = false;
      setError(null);
      const redirectUrl = `${window.location.origin}/`;

      console.log('[SignUp V314.2] Starting sign-up process...');

      // ========================================================================
      // STEP 1: GENERATE NONCE
      // ========================================================================
      const nonce = await IntegrityService.generateNonce(email);
      console.log('[SignUp V314.2] Step 1: Nonce generated');

      // ========================================================================
      // STEP 2: REQUEST INTEGRITY TOKEN (Android only)
      // ========================================================================
      let integrityStatus: IntegrityStatus = 'UNKNOWN';
      let integrityReasons: string[] = [];
      let deviceVerdicts: string[] = [];

      if (Capacitor.getPlatform() === 'android') {
        console.log('[SignUp V314.2] Step 2: Requesting integrity token...');

        const tokenResult = await IntegrityService.requestIntegrityTokenWithRetry(nonce);

        if (tokenResult.token) {
          // V315.6: No auth-time verify-play-integrity here – single source is PlayIntegrityService.run('signup') after getSession() when session exists.
          integrityStatus = 'UNKNOWN';
          integrityReasons = [];
          deviceVerdicts = [];
          console.log('[SignUp V315.6] Token obtained; server verify deferred to post-signUp (PlayIntegrityService.run("signup") when session exists).');
        } else {
          console.warn('[SignUp V314.2] No token obtained:', tokenResult.error);
          // Still allow sign-up - graceful degradation
          integrityStatus = 'TOKEN_UNAVAILABLE';
        }
      } else {
        console.log('[SignUp V314.2] Web platform - skipping integrity check');
      }

      // ========================================================================
      // STEP 4: CREATE USER ACCOUNT (ALWAYS ALLOW)
      // ========================================================================
      console.log('[SignUp V314.2] Step 4: Creating user account...');

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            // Store integrity metadata
            integrity_status: integrityStatus,
            integrity_reasons: integrityReasons,
            device_verdicts: deviceVerdicts,
            platform: Capacitor.getPlatform(),
            signup_timestamp: Date.now(),
            signup_version: 'V314.2'
          }
        }
      });

      // ========================================================================
      // STEP 5: SUCCESS
      // ========================================================================
      if (!error) {
        console.log('[SignUp V314.2] ✅ Sign-up successful');
        console.log('[SignUp V314.2] Integrity Status:', integrityStatus);

        // V315.5: If email confirmation disabled, session may exist immediately – queue integrity
        const { data: { session: postSignUpSession } } = await supabase.auth.getSession();
        if (postSignUpSession && PlayIntegrityService.shouldRun()) {
          console.log('[Auth] signUp -> session exists (email confirm disabled), integrity signup check queued');
          // V316.6: Added client_source tag to prove call-site
          PlayIntegrityService.run('signup', 'auth_signup')
            .then((r) => {
              if (r != null) console.log('[Integrity] signup result ok=', r.ok, 'reason=', r.reason, 'check_id=', r.check_id ?? '');
            })
            .catch((e) => console.error('[Integrity] signup failed', e));
        } else if (!postSignUpSession) {
          console.log('[Auth] No session after signUp; waiting for user to confirm email and sign in.');
        } else if (AUTH_PI_DEBUG) {
          console.log('[Auth] signUp -> integrity skipped (shouldRun=false)');
        }
      }

      return { error };
    } catch (err: any) {
      console.error('[SignUp V314.2] Sign-up error:', err);
      const error = err.message || 'Sign up failed';
      setError(error);
      return { error };
    }
  }, []);

  // V330: Enhanced googleSignIn with auth guard and forced account selection
  const googleSignIn = useCallback(async () => {
    console.log('🔐 V330: ========== GOOGLE SIGN IN START ==========');
    suppressSessionRestoreRef.current = false;

    // V330: Guard against overlapping auth attempts
    if (isAuthenticating) {
      console.log('🔐 V330: ❌ Auth already in progress, blocking duplicate attempt');
      return { error: { message: 'Authentication already in progress' } };
    }

    try {
      setIsAuthenticating(true);
      setError(null);

      // V330: Verify no existing session before starting OAuth
      console.log('🔐 V330: Step 1 - Checking for existing session...');
      const { data: { session: existingSession } } = await supabase.auth.getSession();
      if (existingSession) {
        console.log('🔐 V330: ⚠️ WARNING - Existing session found!');
        console.log('🔐 V330: Existing user:', existingSession.user?.id);
        console.log('🔐 V330: This might be a stale session - proceeding anyway');
      } else {
        console.log('🔐 V330: Step 1 - ✅ No existing session (clean state)');
      }

      // V212.1: Use platform-aware redirect URL helper
      const { getOAuthRedirectTo } = await import('@/lib/auth-helpers');
      const redirectTo = getOAuthRedirectTo();

      console.log('🔐 V330: Step 2 - Initiating OAuth with Google...');
      console.log('🔐 V330: Redirect URL:', redirectTo);
      console.log('🔐 V330: Query params: prompt=select_account (force account picker)');

      // V334: iOS App Store fix — branch on platform to avoid external Safari
      const platform = Capacitor.getPlatform();

      if (platform === 'ios') {
        // V334: On iOS native, get the OAuth URL without auto-redirecting, then
        // open it via Capacitor Browser (SFSafariViewController) so auth stays
        // inside the app. Apple rejected the build when external Safari was used.
        console.log('🔐 V334: iOS detected — using Capacitor Browser (SFSafariViewController)');
        const { data: oauthData, error: oauthUrlError } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo,
            skipBrowserRedirect: true, // V334: Prevents Supabase from calling window.location
            queryParams: {
              access_type: 'offline',
              prompt: 'select_account',
            }
          }
        });

        if (oauthUrlError || !oauthData?.url) {
          console.error('🔐 V334: ❌ Failed to get OAuth URL:', oauthUrlError);
          setIsAuthenticating(false);
          return { error: oauthUrlError || { message: 'Failed to get OAuth URL' } };
        }

        // Diagnostic guard: ensure OAuth URL host matches configured Supabase project.
        try {
          const configuredHost = new URL(import.meta.env.VITE_SUPABASE_URL).hostname.toLowerCase();
          const oauthHost = new URL(oauthData.url).hostname.toLowerCase();
          if (oauthHost !== configuredHost) {
            console.error('🔐 V341: ❌ OAuth host mismatch', { configuredHost, oauthHost });
            setIsAuthenticating(false);
            return { error: { message: `OAuth host mismatch: expected ${configuredHost}, got ${oauthHost}` } };
          }
        } catch (hostErr) {
          console.warn('🔐 V341: OAuth host validation skipped due to parse error', hostErr);
        }

        const { Browser } = await import('@capacitor/browser');
        // V334: Register dismiss listener BEFORE open; iOS may fire before appUrlOpen — grace window.
        const browserDismissHandle = await Browser.addListener('browserFinished', () => {
          console.error('[AUTH] browserFinished while oauthPending=true grace (useAuth google)');
          setTimeout(async () => {
            try {
              const { data } = await supabase.auth.getSession();
              if (data?.session) {
                browserDismissHandle.remove();
                return;
              }
            } catch { /* ignore */ }
            if (blockPrematureIOSOAuthClose('useAuth_google_browserFinished_grace_reset')) {
              return;
            }
            setIsAuthenticating(false);
            browserDismissHandle.remove();
          }, 1500);
        });
        console.error('[AUTH] oauth start platform=ios BEFORE_BROWSER_OPEN');
        setIOSOAuthLock('useAuth_google_before_browser_open');
        try {
          localStorage.setItem('blissnet_ios_oauth_browser_open_attempt', String(Date.now()));
        } catch {}
        await Browser.open({ url: oauthData.url, presentationStyle: 'fullscreen' });
        console.log('🔐 V334: ✅ OAuth URL opened in SFSafariViewController (in-app browser)');
        console.log('🔐 V334: ========== GOOGLE SIGN IN IN-APP BROWSER OPENED ==========');
        // isAuthenticating will be reset by onAuthStateChange (success) or browserFinished (cancel)
        return { error: null };
      }

      // V330: Web + Android — keep existing behavior (Supabase redirects window.location)
      // V330: CRITICAL - Add prompt=select_account to force Google account picker
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account', // V330: Force account selection every time
          }
        }
      });

      if (error) {
        console.error('🔐 V330: ❌ OAuth error:', error);
        setIsAuthenticating(false);
        return { error };
      }

      console.log('🔐 V330: Step 2 - ✅ OAuth redirect initiated');
      console.log('🔐 V330: ========== GOOGLE SIGN IN REDIRECT ==========');
      // Note: isAuthenticating will be reset by onAuthStateChange or on error
      return { error: null };
    } catch (err: any) {
      const error = err.message || 'Google sign in failed';
      console.error('🔐 V330: ❌ Exception during sign in:', error);
      setError(error);
      setIsAuthenticating(false);
      return { error };
    }
  }, [isAuthenticating]);

  // V336: Sign in with Apple via Supabase OAuth (web flow) on all platforms.
  // This avoids native id_token audience mismatch on iOS by using Apple Service ID.
  const appleSignIn = useCallback(async () => {
    console.log('🍎 V335: ========== APPLE SIGN IN START ==========');
    suppressSessionRestoreRef.current = false;

    if (isAuthenticating) {
      console.log('🍎 V335: ❌ Auth already in progress, blocking duplicate attempt');
      return { error: { message: 'Authentication already in progress' } };
    }

    try {
      setIsAuthenticating(true);
      setError(null);

      const platform = Capacitor.getPlatform();
      const isNative = Capacitor.isNativePlatform();
      console.log('🍎 V335: Platform:', platform, 'isNative:', isNative);

      if (isNative && platform === 'ios') {
        const rawNonce = crypto.randomUUID();
        const encoder = new TextEncoder();
        const nonceData = encoder.encode(rawNonce);
        const hashBuffer = await crypto.subtle.digest('SHA-256', nonceData);
        const hashedNonce = Array.from(new Uint8Array(hashBuffer))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');

        console.error('[AUTH] 🍏 Native Apple Start');

        const res = await SignInWithApple.authorize({
          scopes: 'email name',
          nonce: hashedNonce,
        });

        const identityToken = res?.response?.identityToken;

        console.error('[AUTH] 🍏 identityToken received', !!identityToken);

        if (!identityToken) {
          throw new Error('No identity token from Apple');
        }

        const { data, error } = await supabase.auth.signInWithIdToken({
          provider: 'apple',
          token: identityToken,
          nonce: rawNonce,
        });

        console.error('[AUTH] 🍏 Supabase success', !!data, 'error=', error?.message);

        if (error) {
          setIsAuthenticating(false);
          return { error };
        }

        return { error: null };
      }

      if (platform === 'android') {
        const redirectTo = 'com.blissnet.app://auth-callback';
        console.error(`[AUTH_APPLE_ANDROID_OAUTH_BROWSER] redirectTo=${redirectTo}`);
        const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
          provider: 'apple',
          options: {
            redirectTo,
            skipBrowserRedirect: true,
          },
        });

        if (oauthError) {
          console.error('🍎 V335: ❌ OAuth error:', oauthError);
          setIsAuthenticating(false);
          return { error: oauthError };
        }
        if (!data?.url) {
          setIsAuthenticating(false);
          return { error: { message: 'Apple OAuth URL missing' } };
        }

        const { Browser } = await import('@capacitor/browser');
        await Browser.open({ url: data.url, presentationStyle: 'fullscreen' });
        console.error('[AUTH_OAUTH_BROWSER_OPENED] provider=apple');
        return { error: null };
      }

      const { getOAuthRedirectTo } = await import('@/lib/auth-helpers');
      const redirectTo = getOAuthRedirectTo();
      console.log(
        `🍎 V336: Using Supabase Apple OAuth redirect flow platform=${platform} redirectTo=${redirectTo}`
      );

      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: { redirectTo },
      });

      if (oauthError) {
        console.error('🍎 V335: ❌ OAuth error:', oauthError);
        setIsAuthenticating(false);
        return { error: oauthError };
      }

      console.log('🍎 V335: ✅ Apple OAuth redirect initiated');
      return { error: null };

    } catch (err: any) {
      const { getAppleSignInErrorMessage } = await import('@/lib/reviewer-auth');
      const errorMsg = getAppleSignInErrorMessage(err);
      if (import.meta.env.DEV) {
        console.log('[AppleReview] native Apple sign-in error', {
          platform: Capacitor.getPlatform(),
          step: 'SignInWithApple.authorize',
        });
      }
      console.error('🍎 V335: ❌ Exception during Apple sign in:', err);
      setError(errorMsg);
      setIsAuthenticating(false);
      return { error: { message: errorMsg } };
    }
  }, [isAuthenticating]);

  // V314.6: Refresh user profile with retry logic for first-signup safety
  const refreshProfile = useCallback(async () => {
    if (!user?.id) {
      if (import.meta.env.DEV) {
        console.log('🎯 V314.6: Cannot refresh profile - no user ID');
      }
      return;
    }

    const MAX_ATTEMPTS = 6; // ~6 seconds total with exponential backoff
    let lastError: any = null;

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      try {
        if (import.meta.env.DEV && attempt > 0) {
          console.log(`🔄 V314.6: Retry ${attempt}/${MAX_ATTEMPTS - 1} - refreshing profile for:`, user.id);
        } else if (import.meta.env.DEV) {
          console.log('🎯 V314.6: Refreshing user profile for:', user.id);
        }

        const profile = await getUserCompleteProfile(user.id);

        if (profile) {
          setUserProfile(profile);
          if (import.meta.env.DEV) {
            console.log('✅ V314.6: Profile refreshed successfully');
          }
          return; // Success - exit retry loop
        }

        // No profile yet - might be DB trigger still processing
        if (attempt < MAX_ATTEMPTS - 1) {
          const delay = Math.min(2000, 200 * Math.pow(2, attempt));
          if (import.meta.env.DEV) {
            console.log(`⏳ V314.6: No profile yet, waiting ${delay}ms before retry...`);
          }
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      } catch (error) {
        lastError = error;
        if (attempt < MAX_ATTEMPTS - 1) {
          const delay = Math.min(2000, 200 * Math.pow(2, attempt));
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All retries exhausted
    console.error('❌ V314.6: Failed to refresh profile after', MAX_ATTEMPTS, 'attempts:', lastError);
  }, [user?.id]);

  // Local-first signOut: clear session immediately, never block public routes on remote/network.
  const signOut = useCallback(async () => {
    logAuthEvent('AUTH_LOGOUT_START');
    const { alreadyInFlight } = authGuardRef.current.beginLogout();
    if (alreadyInFlight) {
      logAuthEvent('AUTH_LOGOUT_DONE', { reason: 'duplicate-ignored' });
      return;
    }

    const priorUserId = user?.id ?? currentUserIdRef.current;

    try {
      // 1) Invalidate authenticated generation + clear local auth state FIRST
      suppressSessionRestoreRef.current = true;
      setUser(null);
      setSession(null);
      setUserProfile(null);
      setIsLoading(false);
      setIsReady(true);
      setAuthResolution('logged_out');
      setIsAuthenticating(false);
      currentUserIdRef.current = null;
      logAuthEvent('AUTH_LOCAL_STATE_CLEARED');
      logAuthEvent('AUTH_PUBLIC_ROUTE_RENDERED');

      // 2) Best-effort local cache cleanup (bounded — never blocks public UI)
      if (priorUserId) {
        try {
          await raceWithTimeout(
            Promise.allSettled([
              pushService.unregisterCurrentToken(priorUserId),
              badgeService.clear(),
            ]).then(() => undefined),
            2_000,
            'logout-cache-timeout'
          );
        } catch {
          /* non-fatal */
        }
        try {
          ChatMessageCache.clearAllForUser(priorUserId);
          clearProfileCaches(priorUserId);
        } catch {
          /* non-fatal */
        }
      }

      trackEvent(AnalyticsEvents.USER_SIGNED_OUT).catch(() => {});
      resetUser().catch(() => {});

      try {
        localStorage.removeItem('blissnet-auth');
        sessionStorage.removeItem('blissnet-auth');
      } catch {
        /* non-fatal */
      }

      // 3) Remote sign-out with timeout — failure/hang must not restore session or splash
      try {
        await raceWithTimeout(
          supabase.auth.signOut(),
          AUTH_REMOTE_SIGNOUT_TIMEOUT_MS,
          'signOut timeout'
        );
        logAuthEvent('AUTH_REMOTE_SIGNOUT_DONE', { ok: true });
      } catch (err) {
        logAuthEvent('AUTH_REMOTE_SIGNOUT_DONE', { ok: false });
        logAuthEvent('AUTH_LOGOUT_ERROR', {
          reason: err instanceof Error ? err.message : 'remote-signout-failed',
        });
      }

      // Ensure local state stays cleared even if a late event tried to restore
      setUser(null);
      setSession(null);
      setUserProfile(null);
      setIsReady(true);
      setIsLoading(false);
      setAuthResolution('logged_out');

      logAuthEvent('AUTH_LOGOUT_DONE');
    } catch (err: any) {
      logAuthEvent('AUTH_LOGOUT_ERROR', {
        reason: err?.message || 'unexpected',
      });
      // Still force public-capable local state
      suppressSessionRestoreRef.current = true;
      setUser(null);
      setSession(null);
      setUserProfile(null);
      setIsReady(true);
      setIsLoading(false);
      setAuthResolution('logged_out');
    } finally {
      authGuardRef.current.endLogout();
    }
  }, [user?.id]);

  const getAuthGeneration = useCallback(
    () => authGuardRef.current.getGeneration(),
    []
  );
  const isAuthGenerationActive = useCallback(
    (generation: number) => authGuardRef.current.isActive(generation),
    []
  );

  /** Call before every intentional login path (email/signup/Google/Apple/invite OAuth). */
  const beginIntentionalAuth = useCallback(() => {
    suppressSessionRestoreRef.current = false;
  }, []);

  // V404: Idempotent reset of the internal auth lock so OAuth UI watchdogs
  // (timeout / cancel / app foreground fallback) can recover the hook state
  // when no auth event will arrive. No-op when not currently authenticating.
  const resetAuthInProgress = useCallback((reason?: string) => {
    setIsAuthenticating((prev) => {
      if (prev) console.log('[V404.AUTH] resetAuthInProgress reason=', reason || 'unspecified');
      return false;
    });
  }, []);

  // 🚨 NUCLEAR STABILIZATION: Memoize context value to prevent excessive re-renders
  const value = useMemo(() => ({
    user,
    session,
    isReady, // 🚀 V97.0 SIMPLIFIED: Single ready state
    isLoading, // Add missing property
    authResolution,
    error,
    userProfile,
    refreshProfile, // V189.1: Expose refreshProfile
    signIn,
    signUp,
    signOut,
    googleSignIn,
    appleSignIn, // V335: Sign in with Apple (App Store Guideline 4.8)
    resetAuthInProgress, // V404: Clear internal isAuthenticating lock from OAuth UI reset paths
    beginIntentionalAuth,
    getAuthGeneration,
    isAuthGenerationActive,
  }), [user, session, isReady, isLoading, authResolution, error, userProfile, refreshProfile, signIn, signUp, signOut, googleSignIn, appleSignIn, resetAuthInProgress, beginIntentionalAuth, getAuthGeneration, isAuthGenerationActive]);

  // V157.3 TASK 4: Provider stability monitoring (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 V157.3: AuthContext provider value changed');
    }
  }, [value]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};


